import { useLayoutEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import { trackError } from '@dispatch/web/telemetry';

/**
 * Stable id of the single managed Bryntum theme `<link>`. There is exactly one
 * such element in `<head>`; its `href` is swapped light↔dark. Keeping one
 * element (never appending) guarantees no accumulation under rapid toggling.
 */
export const BRYNTUM_THEME_LINK_ID = 'bryntum-theme';

/**
 * URLs of the copied Bryntum theme stylesheets. The shell build copies
 * `material3-light.css` / `material3-dark.css` from
 * `@bryntum/schedulerpro` into `assets/bryntum` (see `apps/shell/project.json`),
 * so these resolve to real `<link href>` targets at runtime.
 */
export const BRYNTUM_THEME_HREF = {
  light: 'assets/bryntum/material3-light.css',
  dark: 'assets/bryntum/material3-dark.css',
} as const;

function resolveHref(mode: 'light' | 'dark'): string {
  const base = document.querySelector('base')?.getAttribute('href') ?? '/';
  const prefix = base.endsWith('/') ? base : `${base}/`;
  return `${prefix}${BRYNTUM_THEME_HREF[mode]}`;
}

/**
 * Keeps the Bryntum Scheduler's theme in sync with the active MUI palette mode.
 *
 * Bryntum's own `DomHelper.setTheme` requires a pre-existing themed `<link>` in
 * `<head>` and throws otherwise, so instead of relying on it (or on a
 * webpack-injected `<style>`), this hook owns a single real
 * `<link id="bryntum-theme">` element and swaps its `href` between the copied
 * light/dark stylesheets keyed on `useTheme().palette.mode`.
 *
 * The element is normally pre-seeded in `apps/shell/src/index.html` (light) so
 * the theme resolves before first paint; this hook reuses it by id and only
 * swaps the `href`. If it is ever absent (e.g. a test host with no index.html),
 * the element is created lazily on first run. Either way it is reused and never
 * removed, so exactly one active theme stylesheet exists at all times — rapid
 * toggling cannot accumulate duplicate links.
 */
export function useBryntumTheme(): void {
  const mode = useTheme().palette.mode;

  // useLayoutEffect (not useEffect): on a re-mount of the Scheduler after a
  // mode change elsewhere, this corrects the <link href>/body attribute
  // synchronously before the browser paints, so the grid never shows one frame
  // in the wrong theme.
  useLayoutEffect(() => {
    const desiredMode = mode === 'dark' ? 'dark' : 'light';
    const href = resolveHref(desiredMode);

    const existing = document.getElementById(BRYNTUM_THEME_LINK_ID);
    let link: HTMLLinkElement;
    if (existing instanceof HTMLLinkElement) {
      link = existing;
    } else {
      link = document.createElement('link');
      link.id = BRYNTUM_THEME_LINK_ID;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }

    // A missing/renamed asset would otherwise leave the grid silently unstyled
    // (unresolved var(--b-*)) with nothing logged. Surface it through the
    // existing telemetry path instead. Re-bound each run so it names the href
    // actually being applied.
    link.onerror = (): void => {
      trackError(new Error(`Bryntum theme stylesheet failed to load: ${href}`));
    };

    if (link.getAttribute('href') !== href) {
      link.setAttribute('href', href);
    }

    // Global theme signal for styles.css. Bryntum floats (event tooltips,
    // task-edit editor) mount at a global `b-float-root` OUTSIDE the scheduler
    // element, so scheduler-scoped selectors cannot reach them — the override
    // gate must live on <body>. Set on the element, not toggled off, because a
    // single scheduler owns the theme for the whole app.
    document.body.setAttribute('data-scheduler-theme', desiredMode);
  }, [mode]);
}
